import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import {
  Plus, Trash2, Edit2, X, Check, Calendar, Clock, Users,
  LayoutGrid, ChevronDown, Phone, Mail,
  MapPin, Search, RefreshCw, AlertCircle, List, QrCode, Download,
} from "lucide-react";
import { useAdmin } from "../admin-context";

// ── Constants ────────────────────────────────────────────────────────────────
const SVG_W = 900;
const SVG_H = 500;

const TABLE_DIMS = {
  circle:    { w: 80,  h: 80  },
  rect:      { w: 100, h: 68  },
  rectangle: { w: 128, h: 68  },
};

const STATUS_COLORS = {
  available: { table: "#22c55e", chair: "#16a34a", text: "#fff", glow: "#86efac" },
  reserved:  { table: "#f59e0b", chair: "#d97706", text: "#fff", glow: "#fcd34d" },
  occupied:  { table: "#ef4444", chair: "#dc2626", text: "#fff", glow: "#fca5a5" },
  inactive:  { table: "#cbd5e1", chair: "#94a3b8", text: "#64748b", glow: "#e2e8f0" },
};

const STATUS_STYLES = {
  available: { bg: "bg-emerald-500", label: "Available", text: "text-emerald-700", light: "bg-emerald-50" },
  reserved:  { bg: "bg-amber-500",   label: "Reserved",  text: "text-amber-700",   light: "bg-amber-50"   },
  occupied:  { bg: "bg-red-500",     label: "Occupied",  text: "text-red-700",     light: "bg-red-50"     },
  inactive:  { bg: "bg-gray-400",    label: "Inactive",  text: "text-gray-600",    light: "bg-gray-50"    },
};

const RES_STATUS = {
  pending:   { label: "Pending",   cls: "bg-yellow-100 text-yellow-800"  },
  confirmed: { label: "Confirmed", cls: "bg-blue-100 text-blue-800"      },
  seated:    { label: "Seated",    cls: "bg-purple-100 text-purple-800"  },
  completed: { label: "Completed", cls: "bg-green-100 text-green-800"    },
  cancelled: { label: "Cancelled", cls: "bg-red-100 text-red-800"        },
};

const TODAY = new Date().toISOString().split("T")[0];

// ── Shared small components ──────────────────────────────────────────────────
const Badge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.inactive;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${s.light} ${s.text}`}>
      {s.label}
    </span>
  );
};

const ResBadge = ({ status }) => {
  const s = RES_STATUS[status] || RES_STATUS.pending;
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
};

const Drawer = ({ open, onClose, title, children, width = "max-w-md" }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className={`relative bg-white h-full ${width} w-full shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-right duration-200`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-white flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
};

const FF = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const inp  = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition";
const sel  = inp + " bg-white";

// ── SVG Floor Map (read-only overview for admin) ─────────────────────────────
const svgCoords = (e, svgEl) => {
  const r  = svgEl.getBoundingClientRect();
  const sx = SVG_W / r.width;
  const sy = SVG_H / r.height;
  return { x: (e.clientX - r.left) * sx, y: (e.clientY - r.top) * sy };
};

const AdminCircleTable = ({ table, cx, cy, r, colors, selected, draggable, onSelect, onDragStart }) => {
  const chairR  = 9;
  const armDist = r + chairR + 6;
  const chairs  = [[cx, cy - armDist],[cx + armDist, cy],[cx, cy + armDist],[cx - armDist, cy]];
  return (
    <g style={{ cursor: draggable ? "grab" : "pointer" }}
      onMouseDown={(e) => { e.preventDefault(); onDragStart(e, table); }}
      onClick={() => onSelect(table)}>
      {selected && <circle cx={cx} cy={cy} r={r + 13} fill="none" stroke={colors.glow} strokeWidth={6} opacity={0.75} />}
      {chairs.map(([x, y], i) => <circle key={i} cx={x} cy={y} r={chairR} fill={colors.chair} />)}
      <circle cx={cx} cy={cy} r={r} fill={colors.table} />
      {selected && <circle cx={cx} cy={cy} r={r} fill="none" stroke="#fff" strokeWidth={2.5} />}
      <text x={cx} y={cy - 5} textAnchor="middle" fill={colors.text} fontSize={10} fontWeight="bold" fontFamily="sans-serif">{table.name}</text>
      <text x={cx} y={cy + 8} textAnchor="middle" fill={colors.text} fontSize={9} opacity={0.85} fontFamily="sans-serif">{table.capacity} pax</text>
    </g>
  );
};

const AdminRectTable = ({ table, tx, ty, tw, th, colors, selected, draggable, onSelect, onDragStart }) => {
  const cx = tx + tw / 2, cy = ty + th / 2;
  const cW = 16, cH = 10, gap = 5;
  const count = Math.max(1, Math.floor(tw / 28));
  const spacing = tw / (count + 1);
  const tops = Array.from({ length: count }, (_, i) => tx + spacing * (i + 1));
  return (
    <g style={{ cursor: draggable ? "grab" : "pointer" }}
      onMouseDown={(e) => { e.preventDefault(); onDragStart(e, table); }}
      onClick={() => onSelect(table)}>
      {selected && <rect x={tx - 8} y={ty - 8} width={tw + 16} height={th + 16} rx={14} fill="none" stroke={colors.glow} strokeWidth={6} opacity={0.75} />}
      {tops.map((x, i) => <rect key={`t${i}`} x={x - cW / 2} y={ty - cH - gap} width={cW} height={cH} rx={4} fill={colors.chair} />)}
      {tops.map((x, i) => <rect key={`b${i}`} x={x - cW / 2} y={ty + th + gap} width={cW} height={cH} rx={4} fill={colors.chair} />)}
      <rect x={tx - cH - gap} y={cy - cW / 2} width={cH} height={cW} rx={4} fill={colors.chair} />
      <rect x={tx + tw + gap}  y={cy - cW / 2} width={cH} height={cW} rx={4} fill={colors.chair} />
      <rect x={tx} y={ty} width={tw} height={th} rx={8} fill={colors.table} />
      {selected && <rect x={tx} y={ty} width={tw} height={th} rx={8} fill="none" stroke="#fff" strokeWidth={2.5} />}
      <text x={cx} y={cy - 5} textAnchor="middle" fill={colors.text} fontSize={10} fontWeight="bold" fontFamily="sans-serif">{table.name}</text>
      <text x={cx} y={cy + 8} textAnchor="middle" fill={colors.text} fontSize={9} opacity={0.85} fontFamily="sans-serif">{table.capacity} pax</text>
    </g>
  );
};

const AdminFloorMap = ({ tables, selectedId, onSelect, onDragEnd, draggable }) => {
  const svgRef = useRef(null);
  const handleDragStart = useCallback((e, table) => {
    if (!draggable) return;
    const svg = svgRef.current;
    const dims = TABLE_DIMS[table.shape] || TABLE_DIMS.circle;
    const start = svgCoords(e, svg);
    const ox = start.x - (table.position_x ?? 100);
    const oy = start.y - (table.position_y ?? 100);
    const move = (me) => {
      const { x, y } = svgCoords(me, svg);
      onDragEnd?.(table.id, Math.max(0, Math.min(SVG_W - dims.w, x - ox)), Math.max(0, Math.min(SVG_H - dims.h, y - oy)), false);
    };
    const up = (me) => {
      const { x, y } = svgCoords(me, svg);
      onDragEnd?.(table.id, Math.max(0, Math.min(SVG_W - dims.w, x - ox)), Math.max(0, Math.min(SVG_H - dims.h, y - oy)), true);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup",   up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup",   up);
  }, [draggable, onDragEnd]);

  return (
    <div className="w-full overflow-x-auto">
      <svg ref={svgRef} viewBox={`0 0 ${SVG_W} ${SVG_H}`} className="w-full rounded-2xl select-none" style={{ minWidth: 540 }}>
        <defs>
          <radialGradient id="adminFloor" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="#fffbeb" />
            <stop offset="100%" stopColor="#fef3c7" />
          </radialGradient>
        </defs>
        <rect x={0} y={0} width={SVG_W} height={SVG_H} fill="url(#adminFloor)" />
        <rect x={10} y={10} width={SVG_W - 20} height={SVG_H - 20} rx={16} fill="none" stroke="#d97706" strokeWidth={3} opacity={0.35} />
        <text x={30} y={40} fill="#d97706" fontSize={11} fontWeight="600" opacity={0.5} fontFamily="sans-serif" letterSpacing="2">
          RESTAURANT FLOOR{draggable && " · drag tables to position"}
        </text>
        <rect x={SVG_W - 120} y={20} width={100} height={32} rx={8} fill="#92400e" opacity={0.15} />
        <text x={SVG_W - 70} y={40} textAnchor="middle" fill="#92400e" fontSize={9} fontWeight="600" opacity={0.5} fontFamily="sans-serif">BAR</text>
        <rect x={20} y={SVG_H - 52} width={80} height={32} rx={8} fill="#92400e" opacity={0.1} />
        <text x={60} y={SVG_H - 32} textAnchor="middle" fill="#92400e" fontSize={9} fontWeight="600" opacity={0.5} fontFamily="sans-serif">ENTRANCE</text>
        {[[36, 70], [SVG_W - 36, 70], [36, SVG_H - 70], [SVG_W - 36, SVG_H - 70]].map(([px, py], i) => (
          <g key={i}><circle cx={px} cy={py} r={12} fill="#86efac" opacity={0.25} /><circle cx={px} cy={py} r={7} fill="#22c55e" opacity={0.2} /></g>
        ))}
        {tables.map((table) => {
          const dims = TABLE_DIMS[table.shape] || TABLE_DIMS.circle;
          const colors = STATUS_COLORS[table.status] || STATUS_COLORS.available;
          const selected = selectedId === table.id;
          const px = Number(table.position_x ?? 140), py = Number(table.position_y ?? 140);
          if (table.shape === "circle") {
            return <AdminCircleTable key={table.id} table={table} cx={px + dims.w / 2} cy={py + dims.h / 2} r={26} colors={colors} selected={selected} draggable={draggable} onSelect={(t) => onSelect(t.id === selectedId ? null : t)} onDragStart={handleDragStart} />;
          }
          return <AdminRectTable key={table.id} table={table} tx={px + 14} ty={py + 14} tw={dims.w - 28} th={dims.h - 28} colors={colors} selected={selected} draggable={draggable} onSelect={(t) => onSelect(t.id === selectedId ? null : t)} onDragStart={handleDragStart} />;
        })}
        {tables.length === 0 && (
          <>
            <text x={SVG_W / 2} y={SVG_H / 2 - 14} textAnchor="middle" fill="#d97706" fontSize={28} opacity={0.25} fontFamily="sans-serif">🍽</text>
            <text x={SVG_W / 2} y={SVG_H / 2 + 14} textAnchor="middle" fill="#d97706" fontSize={13} opacity={0.35} fontFamily="sans-serif">No tables yet — add one below</text>
          </>
        )}
      </svg>
    </div>
  );
};

// ── Table Form Drawer ────────────────────────────────────────────────────────
const TableForm = ({ open, onClose, initialData, onSave }) => {
  const [form, setForm] = useState({ name: "", capacity: 4, shape: "circle", status: "available", position_x: 100, position_y: 100, license_id: 2, ...initialData });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    await onSave({ ...form, capacity: Number(form.capacity) });
    setSaving(false);
    onClose();
  };
  return (
    <Drawer open={open} onClose={onClose} title={initialData?.id ? "Edit Table" : "Add Table"}>
      <div className="space-y-4">
        <FF label="Table Name" required><input className={inp} value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Table A1" /></FF>
        <FF label="Capacity (seats)" required><input className={inp} type="number" min={1} max={20} value={form.capacity} onChange={e => set("capacity", e.target.value)} /></FF>
        <FF label="Shape">
          <select className={sel} value={form.shape} onChange={e => set("shape", e.target.value)}>
            <option value="circle">Circle (2–4 seats)</option>
            <option value="rect">Square (4 seats)</option>
            <option value="rectangle">Rectangle (6–8 seats)</option>
          </select>
        </FF>
        <FF label="Status">
          <select className={sel} value={form.status} onChange={e => set("status", e.target.value)}>
            <option value="available">Available</option>
            <option value="reserved">Reserved</option>
            <option value="occupied">Occupied</option>
            <option value="inactive">Inactive</option>
          </select>
        </FF>
        <div className="pt-2 flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name.trim()} className="flex-1 px-4 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-50 transition flex items-center justify-center gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
            {initialData?.id ? "Update" : "Add Table"}
          </button>
        </div>
      </div>
    </Drawer>
  );
};

// ── Floor Plan Tab ───────────────────────────────────────────────────────────
const FloorPlanTab = () => {
  const { tables, addTable, updateTable, deleteTable, reservations, showToast } = useAdmin();
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [dragPos, setDragPos] = useState({});

  const displayTables = tables.map(t => ({
    ...t,
    position_x: dragPos[t.id]?.x ?? t.position_x ?? 100,
    position_y: dragPos[t.id]?.y ?? t.position_y ?? 100,
  }));

  const handleDragEnd = useCallback((id, x, y, commit) => {
    setDragPos(prev => ({ ...prev, [id]: { x, y } }));
    if (commit) updateTable(id, { position_x: x, position_y: y });
  }, [updateTable]);

  // Count today's reservations per table_name
  const todayResCounts = useMemo(() => {
    const map = {};
    reservations.forEach(r => {
      if ((r.date || "").slice(0, 10) === TODAY && r.table_name && r.status !== "cancelled") {
        map[r.table_name] = (map[r.table_name] || 0) + 1;
      }
    });
    return map;
  }, [reservations]);

  const selectedTable = tables.find(t => t.id === selected?.id);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex flex-wrap items-center gap-3">
          {Object.entries(STATUS_COLORS).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5 text-xs text-gray-600 capitalize">
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: v.table }} />
              {STATUS_STYLES[k]?.label || k}
            </div>
          ))}
        </div>
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-xl text-sm font-semibold hover:bg-orange-600 transition shadow-sm">
          <Plus size={16} /> Add Table
        </button>
      </div>

      <AdminFloorMap
        tables={displayTables}
        selectedId={selected?.id}
        onSelect={(t) => setSelected(t ? (t.id === selected?.id ? null : t) : null)}
        onDragEnd={handleDragEnd}
        draggable
      />

      {selectedTable && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full flex items-center justify-center text-white flex-shrink-0" style={{ background: STATUS_COLORS[selectedTable.status]?.table || "#94a3b8" }}>
            <Users size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-gray-900 text-base">{selectedTable.name}</p>
            <div className="flex items-center gap-3 mt-0.5 flex-wrap">
              <span className="text-xs text-gray-500 flex items-center gap-1"><Users size={12} /> {selectedTable.capacity} seats</span>
              <span className="text-xs text-gray-500 capitalize">{selectedTable.shape}</span>
              <Badge status={selectedTable.status} />
              {todayResCounts[selectedTable.name] > 0 && (
                <span className="text-xs text-orange-600 font-semibold">{todayResCounts[selectedTable.name]} booking(s) today</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => setShowEdit(true)} className="p-2 text-gray-500 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition"><Edit2 size={16} /></button>
            <button onClick={() => setDeleteId(selectedTable.id)} className="p-2 text-gray-500 hover:bg-red-50 hover:text-red-600 rounded-lg transition"><Trash2 size={16} /></button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {tables.map(t => {
          const c = STATUS_COLORS[t.status] || STATUS_COLORS.available;
          return (
            <button key={t.id} onClick={() => setSelected(t.id === selected?.id ? null : t)} className={`p-3 rounded-xl border text-left transition ${selected?.id === t.id ? "border-orange-300 bg-orange-50 shadow-sm" : "border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm"}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: c.table }} />
                <span className="text-sm font-bold text-gray-900 truncate">{t.name}</span>
              </div>
              <p className="text-xs text-gray-500">{t.capacity} seats · {t.shape}</p>
              {todayResCounts[t.name] > 0 && <p className="text-xs text-orange-600 font-semibold mt-0.5">{todayResCounts[t.name]} today</p>}
            </button>
          );
        })}
      </div>

      <TableForm open={showAdd} onClose={() => setShowAdd(false)} onSave={async (d) => { await addTable(d); showToast("Table added"); }} />
      {selectedTable && (
        <TableForm open={showEdit} onClose={() => setShowEdit(false)} initialData={selectedTable} onSave={async (d) => { await updateTable(d.id, d); setSelected(null); showToast("Table updated"); }} />
      )}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-3"><AlertCircle size={24} className="text-red-500 flex-shrink-0" /><h3 className="text-base font-bold text-gray-900">Delete Table?</h3></div>
            <p className="text-sm text-gray-600 mb-5">This will permanently remove the table from the floor plan.</p>
            <div className="flex gap-2">
              <button onClick={() => setDeleteId(null)} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition">Cancel</button>
              <button onClick={async () => { await deleteTable(deleteId); setDeleteId(null); setSelected(null); showToast("Table deleted"); }} className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 transition">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Reservation Form Drawer ──────────────────────────────────────────────────
const ReservationForm = ({ open, onClose, initialData, tables, onSave }) => {
  const [form, setForm] = useState({
    customer_name: "", customer_phone: "", customer_email: "",
    party_size: 2, date: TODAY, time: "19:00",
    table_name: "", notes: "", status: "pending",
    ...initialData,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Reset form when initialData changes
  useEffect(() => {
    setForm({
      customer_name: "", customer_phone: "", customer_email: "",
      party_size: 2, date: TODAY, time: "19:00",
      table_name: "", notes: "", status: "pending",
      ...initialData,
    });
  }, [initialData, open]);

  const handleSave = async () => {
    if (!form.customer_name.trim() || !form.date || !form.time) return;
    setSaving(true);
    await onSave({ ...form, party_size: Number(form.party_size) });
    setSaving(false);
    onClose();
  };

  return (
    <Drawer open={open} onClose={onClose} title={initialData?.id ? "Edit Reservation" : "New Reservation"}>
      <div className="space-y-4">
        <FF label="Guest Name" required>
          <input className={inp} value={form.customer_name} onChange={e => set("customer_name", e.target.value)} placeholder="Full name" />
        </FF>
        <div className="grid grid-cols-2 gap-3">
          <FF label="Phone"><input className={inp} value={form.customer_phone} onChange={e => set("customer_phone", e.target.value)} placeholder="+1 234 567" /></FF>
          <FF label="Email"><input className={inp} type="email" value={form.customer_email} onChange={e => set("customer_email", e.target.value)} placeholder="guest@email.com" /></FF>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FF label="Date" required><input className={inp} type="date" value={form.date} onChange={e => set("date", e.target.value)} /></FF>
          <FF label="Time" required><input className={inp} type="time" value={form.time} onChange={e => set("time", e.target.value)} /></FF>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FF label="Party Size">
            <input className={inp} type="number" min={1} max={20} value={form.party_size} onChange={e => set("party_size", e.target.value)} />
          </FF>
          <FF label="Table">
            <select className={sel} value={form.table_name || ""} onChange={e => set("table_name", e.target.value)}>
              <option value="">Any / walk-in</option>
              {tables.map(t => <option key={t.id} value={t.name}>{t.name} (cap: {t.capacity})</option>)}
            </select>
          </FF>
        </div>
        <FF label="Status">
          <select className={sel} value={form.status} onChange={e => set("status", e.target.value)}>
            {Object.entries(RES_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </FF>
        <FF label="Notes">
          <textarea className={inp + " resize-none"} rows={3} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Special requests, allergies…" />
        </FF>
        <div className="pt-2 flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.customer_name.trim() || !form.date || !form.time} className="flex-1 px-4 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-50 transition flex items-center justify-center gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
            {initialData?.id ? "Update" : "Book"}
          </button>
        </div>
      </div>
    </Drawer>
  );
};

// ── Reservations Tab ─────────────────────────────────────────────────────────
const ReservationsTab = () => {
  const { reservations, tables, addReservation, updateReservation, deleteReservation, loadReservations, showToast } = useAdmin();
  const [filterDate, setFilterDate] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const filtered = useMemo(() => {
    return reservations.filter(r => {
      const matchDate   = !filterDate || (r.date || "").slice(0, 10) === filterDate;
      const matchStatus = filterStatus === "all" || r.status === filterStatus;
      const q = search.toLowerCase();
      const matchSearch = !search
        || r.customer_name?.toLowerCase().includes(q)
        || r.customer_email?.toLowerCase().includes(q)
        || r.customer_phone?.includes(q)
        || r.table_name?.toLowerCase().includes(q);
      return matchDate && matchStatus && matchSearch;
    });
  }, [reservations, filterDate, filterStatus, search]);

  const stats = useMemo(() => {
    const base = filterDate ? reservations.filter(r => (r.date || "").slice(0, 10) === filterDate) : reservations;
    return {
      total:     base.length,
      pending:   base.filter(r => r.status === "pending").length,
      confirmed: base.filter(r => r.status === "confirmed").length,
      seated:    base.filter(r => r.status === "seated").length,
    };
  }, [reservations, filterDate]);

  const handleAdd = async (data) => {
    await addReservation(data);
    showToast("Reservation booked");
  };

  const handleEdit = async (data) => {
    await updateReservation(data.id, data);
    setEditing(null);
    showToast("Reservation updated");
  };

  const handleDelete = async () => {
    await deleteReservation(deleteId);
    setDeleteId(null);
    showToast("Reservation deleted");
  };

  const handleStatusChange = async (r, status) => {
    await updateReservation(r.id, { status });
    showToast(`Marked as ${status}`);
  };

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "Total",     value: stats.total,     color: "bg-blue-500"   },
          { label: "Pending",   value: stats.pending,   color: "bg-yellow-500" },
          { label: "Confirmed", value: stats.confirmed, color: "bg-green-500"  },
          { label: "Seated",    value: stats.seated,    color: "bg-purple-500" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-3 flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg ${s.color} flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>{s.value}</div>
            <span className="text-xs font-medium text-gray-600">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2">
          <Calendar size={14} className="text-gray-400" />
          <input type="date" value={filterDate} onChange={e => { setFilterDate(e.target.value); loadReservations({ date: e.target.value || undefined }); }}
            className="text-sm text-gray-700 outline-none" />
          {filterDate && (
            <button onClick={() => { setFilterDate(""); loadReservations(); }} className="text-gray-400 hover:text-gray-600 transition">
              <X size={12} />
            </button>
          )}
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-orange-300">
          <option value="all">All statuses</option>
          {Object.entries(RES_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 flex-1 min-w-36">
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search guests…" className="text-sm text-gray-700 outline-none w-full" />
        </div>
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-xl text-sm font-semibold hover:bg-orange-600 transition shadow-sm ml-auto">
          <Plus size={16} /> New Booking
        </button>
      </div>

      {/* List */}
      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <Calendar size={40} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm font-medium">No reservations found</p>
          </div>
        )}
        {filtered.map(r => (
          <div key={r.id} className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3 flex items-center gap-4 hover:shadow-md transition">
            {/* Time badge */}
            <div className="flex-shrink-0 w-16 text-center">
              <p className="text-sm font-bold text-gray-800">{r.time}</p>
              <p className="text-[10px] text-gray-400">{r.date}</p>
            </div>

            {/* Guest info */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900 truncate">{r.customer_name}</p>
              <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                {r.customer_phone && <span className="text-xs text-gray-500 flex items-center gap-1"><Phone size={10} />{r.customer_phone}</span>}
                {r.customer_email && <span className="text-xs text-gray-500 flex items-center gap-1"><Mail size={10} />{r.customer_email}</span>}
                <span className="text-xs text-gray-500 flex items-center gap-1"><Users size={10} />{r.party_size} guests</span>
                {r.table_name && <span className="text-xs text-gray-500 flex items-center gap-1"><MapPin size={10} />{r.table_name}</span>}
              </div>
              {r.notes && <p className="text-xs text-gray-400 mt-0.5 truncate italic">"{r.notes}"</p>}
            </div>

            {/* Status + actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <ResBadge status={r.status} />
              <div className="relative group">
                <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition">
                  <ChevronDown size={14} />
                </button>
                <div className="absolute right-0 mt-1 bg-white rounded-xl border border-gray-100 shadow-xl z-20 min-w-32 py-1 hidden group-hover:block">
                  {Object.entries(RES_STATUS).map(([k, v]) => (
                    <button key={k} onClick={() => handleStatusChange(r, k)} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 text-gray-700 font-medium">
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={() => setEditing(r)} className="p-1.5 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition"><Edit2 size={14} /></button>
              <button onClick={() => setDeleteId(r.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {/* Forms */}
      <ReservationForm open={showAdd} onClose={() => setShowAdd(false)} tables={tables} onSave={handleAdd} />
      <ReservationForm open={!!editing} onClose={() => setEditing(null)} initialData={editing} tables={tables} onSave={handleEdit} />

      {/* Delete confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-3"><AlertCircle size={24} className="text-red-500 flex-shrink-0" /><h3 className="text-base font-bold text-gray-900">Delete Reservation?</h3></div>
            <p className="text-sm text-gray-600 mb-5">This will permanently remove this reservation.</p>
            <div className="flex gap-2">
              <button onClick={() => setDeleteId(null)} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition">Cancel</button>
              <button onClick={handleDelete} className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 transition">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Tables Tab (list management + QR codes) ──────────────────────────────────
const TablesTab = () => {
  const { tables, addTable, updateTable, deleteTable, showToast } = useAdmin();
  const [showAdd,  setShowAdd]  = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [qrTable,  setQrTable]  = useState(null);

  const baseUrl = window.location.origin;

  const qrSrc = (tableName, size = 160) =>
    `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(`${baseUrl}/reservations?table=${encodeURIComponent(tableName)}`)}&margin=10&color=1a1a1a&bgcolor=ffffff`;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {tables.length} table{tables.length !== 1 ? "s" : ""} configured
        </p>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-xl text-sm font-semibold hover:bg-orange-600 transition shadow-sm"
        >
          <Plus size={16} /> Add Table
        </button>
      </div>

      {tables.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <QrCode size={40} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm font-medium">No tables yet</p>
          <p className="text-xs mt-1">Add your first table to generate QR codes</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tables.map(t => (
            <div key={t.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-col gap-3">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: STATUS_COLORS[t.status]?.table || "#94a3b8" }} />
                  <span className="font-bold text-gray-900 text-sm">{t.name}</span>
                </div>
                <Badge status={t.status} />
              </div>
              <p className="text-xs text-gray-500">{t.capacity} seats · {t.shape}</p>

              {/* QR preview */}
              <div className="flex flex-col items-center gap-2 py-1">
                <button
                  onClick={() => setQrTable(t)}
                  className="p-2 border border-gray-100 rounded-xl bg-white shadow-sm hover:shadow-md transition"
                >
                  <img src={qrSrc(t.name)} alt={`QR – ${t.name}`} className="w-28 h-28 rounded" />
                </button>
                <p className="text-[10px] text-gray-400">Scan to book this table</p>
              </div>

              {/* Actions */}
              <div className="flex gap-1 pt-1 border-t border-gray-50">
                <button onClick={() => setQrTable(t)} className="flex-1 flex items-center justify-center gap-1 py-2 text-xs font-semibold text-gray-600 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition">
                  <QrCode size={12} /> QR
                </button>
                <button onClick={() => setEditing(t)} className="flex-1 flex items-center justify-center gap-1 py-2 text-xs font-semibold text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition">
                  <Edit2 size={12} /> Edit
                </button>
                <button onClick={() => setDeleteId(t.id)} className="flex-1 flex items-center justify-center gap-1 py-2 text-xs font-semibold text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition">
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* QR enlarged modal */}
      {qrTable && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setQrTable(null)}>
          <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-xs w-full text-center" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">{qrTable.name}</h3>
              <button onClick={() => setQrTable(null)} className="p-2 hover:bg-gray-100 rounded-full transition">
                <X size={16} className="text-gray-500" />
              </button>
            </div>
            <div className="p-3 border border-gray-100 rounded-2xl inline-block mb-3">
              <img src={qrSrc(qrTable.name, 240)} alt={`QR – ${qrTable.name}`} className="w-48 h-48 rounded" />
            </div>
            <p className="text-xs text-gray-500 mb-1">{qrTable.capacity} seats · {qrTable.shape}</p>
            <p className="text-[10px] text-gray-400 mb-4 font-mono break-all">
              {baseUrl}/reservations?table={encodeURIComponent(qrTable.name)}
            </p>
            <a
              href={qrSrc(qrTable.name, 400)}
              download={`qr-${qrTable.name}.png`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2.5 bg-orange-500 text-white rounded-xl text-sm font-semibold hover:bg-orange-600 transition"
            >
              <Download size={14} /> Download QR Code
            </a>
          </div>
        </div>
      )}

      <TableForm
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onSave={async (d) => { await addTable(d); showToast("Table added"); }}
      />
      {editing && (
        <TableForm
          open
          onClose={() => setEditing(null)}
          initialData={editing}
          onSave={async (d) => { await updateTable(d.id, d); setEditing(null); showToast("Table updated"); }}
        />
      )}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-3">
              <AlertCircle size={24} className="text-red-500 flex-shrink-0" />
              <h3 className="text-base font-bold text-gray-900">Delete Table?</h3>
            </div>
            <p className="text-sm text-gray-600 mb-5">This will permanently remove the table and its QR code.</p>
            <div className="flex gap-2">
              <button onClick={() => setDeleteId(null)} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition">Cancel</button>
              <button
                onClick={async () => { await deleteTable(deleteId); setDeleteId(null); showToast("Table deleted"); }}
                className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main Page ────────────────────────────────────────────────────────────────
const Reservations = () => {
  const [tab, setTab] = useState("reservations");

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Reservations</h1>
        <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
          {[
            { id: "reservations", icon: <List size={15} />,       label: "Bookings"   },
            { id: "floorplan",    icon: <LayoutGrid size={15} />, label: "Floor Plan" },
            { id: "tables",       icon: <QrCode size={15} />,     label: "Tables"     },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
                tab === t.id ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {t.icon}{t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "reservations" && <ReservationsTab />}
      {tab === "floorplan"    && <FloorPlanTab />}
      {tab === "tables"       && <TablesTab />}
    </div>
  );
};

export default Reservations;
